import { ArrowRightOutlined, CalendarOutlined } from "@ant-design/icons";
import {
  Col,
  Image,
  List,
  Pagination,
  Row,
  Segmented,
  Space,
  Typography,
  Tooltip,
  Tag,
  theme,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import useQuery from "../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../helpers/urlFilters";
import { getPaginationProps } from "../../utils/tablePagination";
import { getOutPatientListApi } from "../../services/patient.services";
import { getCookie } from "../../helpers/localStorage";
import { FacilityID } from "../../constants/defaultKeys";
import docIcon from "../../assets/doctorIcons/consultant.svg";
import "./PatientList.css";
import NoDataPage from "../NoDataPages/NoDataPage";
import PatientDataIcon from "../../assets/images/nurseIcons/patientdata.svg";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import { UiButton, UiTag, UiDataTable } from "../ui";
import { formatPatientAge } from "../../helpers/formatAge";
import tagStyles from "../../styles/Custom/Tag.module.css";
import usePageFillTableScroll from "../../hooks/usePageFillTableScroll";
import useIsMobile from "../../hooks/useIsMobile";

const { useToken } = theme;

const OUT_PATIENT_SEGMENTS = ["today", "week", "all"];

const getValidOutSegment = (status) =>
  OUT_PATIENT_SEGMENTS.includes(status) ? status : "today";

const OutPatientsCard = ({ userDetails, role }) => {
  const token = useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQuery();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    () => parseSearchParam(params) ?? ""
  );
  const [activeOutSegment, setActiveOutSegment] = useState(() =>
    getValidOutSegment(params?.status)
  );
  const [outPatientList, setOutPatientList] = useState([]);
  const [pageNo, setPageNo] = useState(() => parsePageParam(params));
  const listWrapRef = useRef(null);
  const ignoreSearchUpdatesRef = useRef(false);
  const isMobile = useIsMobile();
  const listCount = outPatientList?.data?.length ?? 0;
  usePageFillTableScroll(listWrapRef, isMobile, "default", listCount);

  const outPatientLabels = [
    { label: "Today", value: "today" },
    { label: "Week", value: "week" },
    { label: "All", value: "all" },
  ];

  const onOutPatientSegmentChange = (value) => {
    const next = getValidOutSegment(value);
    if (next === activeOutSegment) return;
    // Block stale search-box updates from putting `search` back into the URL
    ignoreSearchUpdatesRef.current = true;
    setActiveOutSegment(next);
    setLoading(true);
    setOutPatientList([]);
    setPageNo(1);
    setSearchQuery("");
    // Clear list filters when switching Today/Week/All
    updateUrlSearchParams(navigate, {
      status: next,
      search: null,
      page: null,
    });
  };

  const facilityId = Number(getCookie(FacilityID)) || userDetails?.currFacility;

  const navigateToOpPatient = (item) => {
    if (role === "clinical-practitioner") {
      navigate(
        `/clinical-practitioner/home?patient-type=outPatients&status=${activeOutSegment}&patient=${item?.id}&type=ia`
      );
      return;
    }
    navigate(
      `/op/home?pid=${item?.patient_id}&aid=${item?.id}&status=${activeOutSegment}`
    );
  };

  // Keep local filters aligned with URL (source of truth after navigations)
  useEffect(() => {
    const nextSegment = getValidOutSegment(params?.status);
    const nextSearch = parseSearchParam(params) ?? "";
    const nextPage = parsePageParam(params);
    setActiveOutSegment((prev) => (prev === nextSegment ? prev : nextSegment));
    setSearchQuery((prev) => (prev === nextSearch ? prev : nextSearch));
    setPageNo((prev) => (prev === nextPage ? prev : nextPage));
    // Search was cleared from the URL (e.g. toggle change) — allow typing again
    if (!nextSearch) {
      ignoreSearchUpdatesRef.current = false;
    }
  }, [location.search, params?.status, params?.search, params?.page]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    let query = `facility=${facilityId}`;
    if (searchQuery) {
      query += `&search=${encodeURIComponent(searchQuery)}`;
    }

    getOutPatientListApi(query, activeOutSegment, pageNo)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;

        // Deduplicate by id to avoid React duplicate key warnings
        if (data?.data) {
          const seen = new Set();
          data.data = data.data.filter((item) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });
        }

        setOutPatientList(data);
      })
      .catch((err) => {
        if (!cancelled) console.log(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, activeOutSegment, pageNo, searchQuery]);

  const onSearchChange = (e) => {
    if (ignoreSearchUpdatesRef.current) return;
    const query = e?.target?.value ?? "";
    setSearchQuery(query);
    setPageNo(1);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setLoading(true);
    setPageNo(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };

  const outPatientFilters = (
    <div className="outpatient-segmented-wrap">
      <Segmented
        className="patient-list-segment outpatient-patient-segment"
        options={outPatientLabels}
        value={activeOutSegment}
        onChange={onOutPatientSegmentChange}
      />
    </div>
  );

  const formatEmrName = (type) => {
    if (!type) return "";

    const key = type.toLowerCase();

    if (key === "cardiopulmonary") {
      return isMobile ? "Cardio EMR" : "Cardiopulmonary EMR";
    }

    const map = {
      neuro: "Neuro EMR",
      ortho: "Ortho EMR",
      cardio: "Cardio EMR",
      sports: "Sports EMR",
      general: "General EMR",
      pediatric: "Pediatric EMR",
    };

    return map[key] || `${type.charAt(0).toUpperCase() + type.slice(1)} EMR`;
  };

  const truncateName = (name) => {
    return name && name.length > 20 ? `${name.slice(0, 20)}...` : name;
  };

  const getAgeGenderTag = (item) => {
    const age = item?.patient_age_by_dob;
    const gender = item?.patient_gender;
    const genderShort =
      gender === "Male"
        ? "M"
        : gender === "Female"
        ? "F"
        : gender === "Other"
        ? "Other"
        : "";
    let ageText = "";
    if (age != null) {
      if (age < 1) {
        ageText = `${Math.round(age * 12)} months`;
      } else {
        ageText = `${Math.floor(age)}`;
      }
    }
    if (ageText && genderShort) return `${ageText} / ${genderShort}`;
    if (ageText) return ageText;
    if (genderShort) return genderShort;
    return "";
  };

  return (
    <Row gutter={[0, 16]}>
      <Col xs={24}>
        <UiDataTable
          showHeader={false}
          shellClassName="ui-table-fill-shell"
          onSearch={onSearchChange}
          searchValue={searchQuery}
          searchPlaceholder="Search for patient"
          toolbarTrailing={outPatientFilters}
        >
          <div ref={listWrapRef} className="ui-table-fill-body">
            <div className="patient-list-scroll">
            <List
              className={[
                "patient-list",
                !(outPatientList?.data?.length > 0) ? "patient-list--empty" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ background: "#fff" }}
              loading={loading}
              header={
                !loading && outPatientList?.data?.length > 0 ? (
                  <div className="patient-list-header op-patient-row op-patient-row--header">
                    <div className="op-patient-row__name">Name</div>
                    <div className="op-patient-row__details">
                      Appointment Details
                    </div>
                    <div className="op-patient-row__action">Action</div>
                  </div>
                ) : null
              }
              pagination={false}
              bordered
              itemLayout="horizontal"
              size="small"
              dataSource={outPatientList?.data || []}
              rowKey={(item) => `${item?.id}-${item?.op_id}`}
              locale={{
                emptyText: loading ? (
                  <LoadingIndicator message="Loading data, please wait..." />
                ) : (
                  <NoDataPage
                    title="No Out Patients Available"
                    subtitle="Please check back later or adjust your filters."
                    image={PatientDataIcon}
                  />
                ),
              }}
              renderItem={(item) => (
                <List.Item
                  className="patient-list-row-clickable"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigateToOpPatient(item)}
                >
                  <div className="op-patient-row">
                    <div className="op-patient-row__name">
                      <div className="op-patient-row__name-main">
                        <Tooltip
                          title={item?.patient_name}
                          placement="top"
                        >
                          <Typography.Text strong>
                            {truncateName(item?.patient_name)}
                          </Typography.Text>
                        </Tooltip>

                        {item?.emr_service_type && (
                          <Tag
                            color="green"
                            className={tagStyles.smTag}
                            style={{
                              textTransform: "capitalize",
                            }}
                          >
                            {formatEmrName(item?.emr_service_type)}
                          </Tag>
                        )}
                      </div>
                      <div className="op-patient-row__name-meta">
                        <Tag>{getAgeGenderTag(item)}</Tag>
                      </div>
                    </div>

                    <div className="op-patient-row__details">
                      <Space wrap size={[8, 8]}>
                        <Tag>OP ID: {item?.op_id}</Tag>

                        <Tag className={tagStyles.smTag} color="purple">
                          <CalendarOutlined style={{ marginRight: "4px" }} />
                          {item?.appointment_date &&
                            dayjs(item?.appointment_date).format("DD MMM, YYYY")}
                        </Tag>

                        <Tag color="blue">
                          <Image
                            src={docIcon}
                            style={{ width: "12px" }}
                            preview={false}
                          />{" "}
                          {item?.doctor_name}
                        </Tag>

                        {item?.appointment_for && (
                          <Tag
                            color={
                              item?.appointment_for === "consultation"
                                ? "purple"
                                : "blue"
                            }
                          >
                            {item?.appointment_for}
                          </Tag>
                        )}
                      </Space>
                    </div>

                    <div className="op-patient-row__action">
                      <Typography.Link
                        style={{
                          color: token.colorPrimary,
                          whiteSpace: "nowrap",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToOpPatient(item);
                        }}
                      >
                        Start Consultation <ArrowRightOutlined />
                      </Typography.Link>
                    </div>
                  </div>
                </List.Item>
              )}
            />
            </div>
            {outPatientList?.total_count > 50 ? (
              <div className="ui-table-shell-pagination">
                <Pagination
                  {...getPaginationProps({
                    current: pageNo,
                    pageSize: 50,
                    total: outPatientList?.total_count,
                    onChange: handlePageChange,
                  })}
                />
              </div>
            ) : null}
          </div>
        </UiDataTable>
      </Col>
    </Row>
  );
};

export default OutPatientsCard;